'use client';

import { TextPassage } from '@petsmart-ui/sparky';
import { useEffect, useRef } from 'react';
import { useShallow } from 'zustand/shallow';

import { trackAutoshipFrequencyCalculatorModal } from '@libs/analytics';
import { getENV, logSentryError, logSentryEvent } from '@libs/common-next';

import { useProduct } from '@/entities/product';
import { SentryErrors, SentryEvents } from '@/shared/sentry';
import { BaseDrawer } from '@/shared/ui';

import { useFrequencyCalculatorDrawerStore, useFrequencyCalculatorForm } from './model';
import {
    CalculatingStatus,
    Footer,
    NumberOfPetsField,
    ProductSummary,
    SuggestedFrequency,
    TotalCupsPerDayField,
} from './ui';

import './styles.scss';

export const FrequencyCalculatorDrawer = () => {
    const { isOpen, close } = useFrequencyCalculatorDrawerStore(
        useShallow(store => ({ isOpen: store.isOpen, close: store.close }))
    );
    const { product } = useProduct();
    const locale = getENV('NEXT_PUBLIC_LOCALE');

    const {
        watch,
        setValue,
        trigger,
        formState: { errors },
        calculationResult,
        handleCalculate,
        handleApplySuggestedFrequency,
        handleFormReset,
        fieldResetKey,
        canReset,
        isCalculateDisabled,
        isCalculating,
    } = useFrequencyCalculatorForm();
    const isResetting = useRef(false);
    const numberOfPets = watch('numberOfPets');
    const totalCupsPerDay = watch('totalCupsPerDay');

    const handleClose = () => {
        trackAutoshipFrequencyCalculatorModal({ link_text: 'close' });
        handleFormReset();
        close();
    };

    const handleResetStart = () => {
        const activeElement = document.activeElement;
        const isFormField = activeElement instanceof HTMLInputElement || activeElement instanceof HTMLSelectElement;

        isResetting.current = isFormField;
        if (isFormField) activeElement.blur();
    };

    const handleReset = () => {
        trackAutoshipFrequencyCalculatorModal({ link_text: 'reset' });

        try {
            handleFormReset();
            logSentryEvent('Reset frequency calculator', {
                sentryContext: SentryEvents.RESET_FREQUENCY_CALCULATOR,
                tags: {
                    component: 'FrequencyCalculator',
                    severity: 'INFO',
                },
                extra: {
                    locale,
                    sku: product?.sku ?? 'unknown',
                    numberOfPets: numberOfPets ?? 'unknown',
                    totalCupsPerDay: totalCupsPerDay ?? 'unknown',
                    recommendedAutoshipFrequency: String(calculationResult?.recommendedAutoshipFrequency ?? 'unknown'),
                    frequencyUnit: calculationResult?.frequencyUnit ?? 'unknown',
                },
            });
        } catch (error) {
            logSentryError(error, {
                ...SentryErrors.FrequencyCalculator.ResetFrequencyCalculatorFailed,
                extra: {
                    ...SentryErrors.FrequencyCalculator.ResetFrequencyCalculatorFailed.extra,
                    locale,
                    sku: product?.sku ?? 'unknown',
                    numberOfPets: numberOfPets ?? 'unknown',
                    totalCupsPerDay: totalCupsPerDay ?? 'unknown',
                    recommendedAutoshipFrequency: String(calculationResult?.recommendedAutoshipFrequency ?? 'unknown'),
                    frequencyUnit: calculationResult?.frequencyUnit ?? 'unknown',
                },
            });
        }
    };

    const handleFieldBlur = (field: 'numberOfPets' | 'totalCupsPerDay') => {
        if (isResetting.current) {
            isResetting.current = false;
            return;
        }

        trigger(field);
    };

    useEffect(() => {
        if (isOpen) {
            handleFormReset();
        }
    }, [isOpen, handleFormReset]);

    return (
        <BaseDrawer
            isOpen={isOpen}
            title="Calculate frequency"
            onCloseAction={handleClose}
            testId="frequency-calculator-drawer"
            className="frequency-calculator-drawer"
        >
            <TextPassage size="lg" className="frequency-calculator-drawer__subtitle">
                Tell us how many pets eat this food and their total cups per day.
            </TextPassage>

            <ProductSummary />

            <NumberOfPetsField
                value={numberOfPets}
                resetKey={fieldResetKey}
                errorMessage={errors.numberOfPets?.message}
                onValueChange={value => {
                    setValue('numberOfPets', value, { shouldDirty: true, shouldValidate: true });
                    trigger('numberOfPets');
                }}
                onFieldBlur={() => handleFieldBlur('numberOfPets')}
            />

            <TotalCupsPerDayField
                value={totalCupsPerDay}
                errorMessage={errors.totalCupsPerDay?.message}
                onValueChange={value => setValue('totalCupsPerDay', value, { shouldDirty: true })}
                onFieldBlur={() => handleFieldBlur('totalCupsPerDay')}
            />

            {isCalculating && !calculationResult && <CalculatingStatus />}
            {calculationResult && <SuggestedFrequency result={calculationResult} />}

            <Footer
                calculationResult={calculationResult}
                isCalculateDisabled={isCalculateDisabled}
                canReset={canReset}
                onCalculate={handleCalculate}
                onApplySuggestedFrequency={handleApplySuggestedFrequency}
                onReset={handleReset}
                onResetStart={handleResetStart}
            />
        </BaseDrawer>
    );
};
